import { Component, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { UntypedFormControl, UntypedFormGroup, Validators } from "@angular/forms";
import { AngularEditorComponent, AngularEditorConfig } from "@kolkov/angular-editor";
import { TranslateService } from "@ngx-translate/core";
import { Observable, Subscription } from "rxjs";
import { SnackbarService } from "src/app/core/service/snackbar.service";
import { UserCustomFieldDTO } from "src/app/data/schema/Subscriptions/UserCustomFieldDTO";
import { PatchUserAccountTO } from "src/app/data/schema/User/PatchUserAccountTO";
import { UsuarioTO } from "src/app/data/schema/User/UsuarioTO";
import { UserCustomFieldsComponent } from "src/app/shared/component/user-custom-fields/user-custom-fields.component";
import { ProfileService } from "../../services/profile.service";

export class DerivedPatchUserAccountTO extends PatchUserAccountTO {
    urlLinkedin: string;
    urlStackOverflow: string;
    urlFacebook: string;
    urlTwitter: string;
}

@Component({
    selector: "app-profile-account",
    templateUrl: "./profile-account.component.html",
    styleUrls: ["./profile-account.component.scss"]
})
export class ProfileAccountComponent implements OnInit, OnDestroy {
    private userSubscription: Subscription;

    public loading$: Observable<boolean>;
    public editMode: boolean;
    public cardData: DerivedPatchUserAccountTO;
    public userCustomFields: Observable<UserCustomFieldDTO[]>;
    public user: UsuarioTO;

    public formGroup: UntypedFormGroup;

    @ViewChild("apresentacao")
    private apresentacao: AngularEditorComponent;

    @ViewChild("appUserCustomFields") userCustomFieldsComponent: UserCustomFieldsComponent;

    constructor(
        private translate: TranslateService,
        private profileService: ProfileService,
        private snackBar: SnackbarService
    ) {
        this.editMode = false;
        this.cardData = {
            nome: "",
            sobrenome: "",
            headline: "",
            apresentacao: "",
            links: [],
            urlLinkedin: "",
            urlStackOverflow: "",
            urlFacebook: "",
            urlTwitter: ""
        };

        this.loading$ = this.profileService.getLoadingAsync();

        this.userSubscription = this.profileService.getUserAsync().subscribe((user) => {
            if (user) {
                this.user = user;

                this.cardData = {
                    nome: user.nome,
                    sobrenome: user.sobrenome,
                    headline: user.headline,
                    apresentacao: user.apresentacao,
                    links: user.links,
                    urlLinkedin: user.links?.find((l) => l.nome == "linkedin")?.url,
                    urlStackOverflow: user.links?.find((l) => l.nome == "stack-overflow")?.url,
                    urlFacebook: user.links?.find((l) => l.nome == "facebook")?.url,
                    urlTwitter: user.links?.find((l) => l.nome == "twitter")?.url,
                    customFields: user.customFields
                };

                if (this.formGroup)
                    this.formGroup.patchValue({
                        nome: this.cardData.nome,
                        sobrenome: this.cardData.sobrenome,
                        headline: this.cardData.headline,
                        apresentacao: this.cardData.apresentacao,
                        urlLinkedin: this.cardData.urlLinkedin,
                        urlStackOverflow: this.cardData.urlStackOverflow,
                        urlFacebook: this.cardData.urlFacebook,
                        urlTwitter: this.cardData.urlTwitter
                    });
            }
        });

        this.userCustomFields = this.profileService.userCustomFieldsAsync();
    }

    editorConfig: AngularEditorConfig = {
        editable: false,
        spellcheck: false,
        height: "auto",
        minHeight: "160px",
        maxHeight: "auto",
        width: "100%",
        minWidth: "0",
        translate: "yes",
        enableToolbar: true,
        showToolbar: true,
        defaultParagraphSeparator: "",
        defaultFontName: "",
        defaultFontSize: "",
        fonts: [],
        customClasses: [],
        uploadUrl: "v1/image",
        uploadWithCredentials: false,
        sanitize: false,
        toolbarPosition: "top",
        toolbarHiddenButtons: [
            [
                "undo",
                "redo",
                "underline",
                "strikeThrough",
                "subscript",
                "superscript",
                "indent",
                "outdent",
                "insertUnorderedList",
                "insertOrderedList",
                "heading",
                "fontName"
            ],
            [
                "fontSize",
                "textColor",
                "backgroundColor",
                "customClasses",
                "link",
                "unlink",
                "insertImage",
                "insertVideo",
                "insertHorizontalRule",
                "removeFormat",
                "toggleEditorMode"
            ]
        ]
    };

    ngOnInit(): void {
        this.profileService.getLoggedUser();

        this.formGroup = new UntypedFormGroup({
            nome: new UntypedFormControl({ value: this.cardData.nome, disabled: !this.editMode }, Validators.required),
            sobrenome: new UntypedFormControl(
                { value: this.cardData.sobrenome, disabled: !this.editMode },
                Validators.required
            ),
            headline: new UntypedFormControl({ value: this.cardData.headline, disabled: !this.editMode }),
            apresentacao: new UntypedFormControl({ value: this.cardData.apresentacao, disabled: !this.editMode }),
            urlLinkedin: new UntypedFormControl({ value: this.cardData.urlLinkedin, disabled: !this.editMode }),
            urlStackOverflow: new UntypedFormControl({ value: this.cardData.urlStackOverflow, disabled: !this.editMode }),
            urlFacebook: new UntypedFormControl({ value: this.cardData.urlFacebook, disabled: !this.editMode }),
            urlTwitter: new UntypedFormControl({ value: this.cardData.urlTwitter, disabled: !this.editMode })
        });
    }

    ngOnDestroy(): void {
        this.userSubscription.unsubscribe();
        this.profileService.clear();
    }

    toggleEdition(): void {
        if (this.editMode) {
            if (!this.formGroup.valid) {
                this.translate.get("MESSAGES.REQUIRED").subscribe((res) => {
                    this.snackBar.showMessage(res);
                });
                return;
            }

            this.apresentacao.config.editable = false;
            this.formGroup.controls["nome"].disable();
            this.formGroup.controls["sobrenome"].disable();
            this.formGroup.controls["headline"].disable();
            this.formGroup.controls["apresentacao"].disable();
            this.formGroup.controls["urlLinkedin"].disable();
            this.formGroup.controls["urlStackOverflow"].disable();
            this.formGroup.controls["urlFacebook"].disable();
            this.formGroup.controls["urlTwitter"].disable();

            this.save();
        } else {
            this.apresentacao.config.editable = true;
            this.formGroup.controls["nome"].enable();
            this.formGroup.controls["sobrenome"].enable();
            this.formGroup.controls["headline"].enable();
            this.formGroup.controls["apresentacao"].enable();
            this.formGroup.controls["urlLinkedin"].enable();
            this.formGroup.controls["urlStackOverflow"].enable();
            this.formGroup.controls["urlFacebook"].enable();
            this.formGroup.controls["urlTwitter"].enable();
        }

        this.editMode = !this.editMode;
    }

    private save(): void {
        this.cardData.nome = this.formGroup.value.nome;
        this.cardData.sobrenome = this.formGroup.value.sobrenome;
        this.cardData.headline = this.formGroup.value.headline;
        this.cardData.apresentacao = this.formGroup.value.apresentacao;
        this.cardData.customFields = this.userCustomFieldsComponent?.exportValues();

        this.cardData.links = [
            {
                nome: "linkedin",
                icone: "linkedin",
                url: this.formGroup.value.urlLinkedin
            },
            {
                nome: "stack-overflow",
                icone: "stack-overflow",
                url: this.formGroup.value.urlStackOverflow
            },
            {
                nome: "facebook",
                icone: "facebook",
                url: this.formGroup.value.urlFacebook
            },
            {
                nome: "twitter",
                icone: "twitter",
                url: this.formGroup.value.urlTwitter
            }
        ];

        this.profileService.updateUserAccount(this.cardData, this.userCustomFieldsComponent?.filesToUpload);
    }
}
